import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { PageHeader, Badge } from "../../components/ui-extras";
import PortalBanner from "../../components/PortalBanner";
import { displayProduct } from "../../lib/products";
import { formatCard } from "../../lib/cardDisplay";
import { Download, FileCsv, FilePdf, FileZip, X, PencilSimple, Trash } from "@phosphor-icons/react";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";

// P4-CUSTOMER-TRANSACTION-REPORT-SCHEDULER — Multi-Schedule Refinement
// (2026-09-16). Same card position + palette + typography as R1;
// evolved into two sections:
//   A. Saved Scheduled Reports — one compact row per schedule with
//      Edit / Enable-Disable / Delete controls.
//   B. Create / Edit form — reuses the R1 controls verbatim; a Cancel
//      Edit affordance returns the form to Create mode.
// Uses the plural API (`/portal/report-schedules`). Idempotency
// markers on the schedule row are never touched by the UI — only the
// backend scheduler pass writes them.
const _DAY_ORDER = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const _DAY_LABEL = { mon: "Monday", tue: "Tuesday", wed: "Wednesday",
                     thu: "Thursday", fri: "Friday", sat: "Saturday", sun: "Sunday" };

function scheduleSummary(s) {
  if (s.frequency === "daily") {
    return { title: "Daily Transaction Report",
             verbal: "Every day",
             period: "Previous day" };
  }
  if (s.frequency === "weekly") {
    const dayKey = s.weekly_day || "mon";
    const idx = _DAY_ORDER.indexOf(dayKey);
    const endKey = _DAY_ORDER[(idx + 6) % 7];
    const dayLabel = _DAY_LABEL[dayKey] || "Monday";
    const endLabel = _DAY_LABEL[endKey] || "Sunday";
    return { title: "Weekly Transaction Report",
             verbal: `Every ${dayLabel}`,
             period: `Previous ${dayLabel}–${endLabel}` };
  }
  if (s.frequency === "monthly") {
    return { title: "Monthly Transaction Report",
             verbal: "1st day of each month",
             period: "Previous calendar month" };
  }
  return { title: s.frequency, verbal: "", period: "" };
}

const _EMPTY_FORM = {
  enabled: true, frequency: "daily", weekly_day: "mon",
  recipient_mode: "my_email", recipient_email: "",
  // P4-PORTAL-SCHEDULE-FILTER-UI R1 (Feb 2026) — additive cohort
  // filters. Empty string ("" → sent as null) means unfiltered / All,
  // preserving backward compatibility with schedules created before
  // this slice.
  network_filter: "", country_filter: "",
};

// P4-PORTAL-SCHEDULE-FILTER-UI CUSTOMER-SCOPED R1 (Feb 2026) —
// SINGLE source of truth for the customer-authorized lane set.
// The Transactions & Downloads dropdown already reuses this exact
// derivation (see `authorizedNetworks` in the parent component
// below); the Schedule form must reuse the SAME helper so we never
// end up with two independent customer-network scoping algorithms.
export function deriveAuthorizedLanes(customerProfile) {
  if (!customerProfile) return [];
  const legacyRaw = customerProfile.price_report_networks || [];
  const legacy = legacyRaw
    .map((n) => (n || "").toLowerCase())
    .filter((n) => n === "esso" || n === "pipeline");
  const lanes = customerProfile.authorized_lanes || [];
  const hasPPCa = lanes.some(
    (l) => (l.network || "").toLowerCase() === "petro_pass"
           && (l.country || "").toUpperCase() === "CA",
  );
  const hasPPUs = lanes.some(
    (l) => (l.network || "").toLowerCase() === "petro_pass"
           && (l.country || "").toUpperCase() === "US",
  );
  const hasTaPetroUs = lanes.some(
    (l) => (l.network || "").toLowerCase() === "ta_petro"
           && (l.country || "").toUpperCase() === "US",
  );
  const out = [];
  if (legacy.includes("esso")) out.push("esso_ca");
  if (legacy.includes("pipeline")) out.push("pipeline");
  if (hasPPCa) out.push("petro_pass_ca");
  if (hasPPUs) out.push("petro_pass_us");
  if (hasTaPetroUs) out.push("ta_petro_us");
  return out;
}

// Map each authorized lane → the canonical (network_filter,
// country_filter) pair the backend `render_customer_transaction_report_csv`
// helper accepts. Mirrors the atomic unpacking already used by the
// Transactions & Downloads network dropdown (see parent component).
const _LANE_TO_SCHEDULE_FILTERS = {
  esso_ca:       { network: "esso",       country: "CA" },
  pipeline:      { network: "pipeline",   country: "CA" },
  petro_pass_ca: { network: "petro_pass", country: "CA" },
  petro_pass_us: { network: "petro_pass", country: "US" },
  ta_petro_us:   { network: "ta_petro",   country: "US" },
};

// COUNTRY-FILTER-PARITY-R1 — one effective (network, country) pair for the
// table and every download. A lane's own country always wins.
const _LANE_COUNTRY = (lane) => (_LANE_TO_SCHEDULE_FILTERS[lane] || {}).country || "";
export function effectiveTxFilters(f) {
  const lane = _LANE_TO_SCHEDULE_FILTERS[f.network];
  const network = f.network ? (lane && f.network !== "pipeline" ? lane.network : f.network) : "";
  const country = (lane && f.network !== "pipeline" ? lane.country : "") || f.country || "";
  return { network, country };
}

// Human-readable labels for the persisted filter values shown in the
// Saved Scheduled Reports table + Schedule form. Never surfaces raw
// backend enum values to customers.
export function humanNetwork(v) {
  const m = { esso: "Esso", pipeline: "Pipeline", ta_petro: "TA/Petro", petro_pass: "Petro-Pass" };
  return m[v] || "All";
}
export function humanCountry(v) {
  const m = { CA: "Canada", US: "USA" };
  return m[v] || "All";
}

// Derive:
//   availableNetworks       — set of `network_filter` values the
//                             customer has AT LEAST ONE lane for.
//   countriesByNetwork[nw]  — set of `country_filter` values the
//                             customer has for that specific network.
//   allCountries            — union of countries across every
//                             authorized lane (used when Network=All).
export function scopedSchedulerFilters(customerProfile) {
  const lanes = deriveAuthorizedLanes(customerProfile);
  const availableNetworks = new Set();
  const countriesByNetwork = {};
  const allCountries = new Set();
  for (const lane of lanes) {
    const pair = _LANE_TO_SCHEDULE_FILTERS[lane];
    if (!pair) continue;
    availableNetworks.add(pair.network);
    if (!countriesByNetwork[pair.network]) countriesByNetwork[pair.network] = new Set();
    countriesByNetwork[pair.network].add(pair.country);
    allCountries.add(pair.country);
  }
  return { availableNetworks, countriesByNetwork, allCountries };
}

function TransactionReportScheduler({ customerProfile }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(_EMPTY_FORM);

  // Customer-scoped filter authority — reuses the SAME derivation the
  // Transactions & Downloads dropdown uses. Never renders a network
  // the customer isn't authorized for.
  const scoped = useMemo(
    () => scopedSchedulerFilters(customerProfile),
    [customerProfile],
  );

  const refresh = async () => {
    try {
      const res = await api.get("/portal/report-schedules");
      setSchedules(res?.data?.schedules || []);
    } catch {
      setSchedules([]);
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      await refresh();
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, []);

  const isValid = useMemo(() => {
    if (!["daily", "weekly", "monthly"].includes(form.frequency)) return false;
    if (form.frequency === "weekly" && !form.weekly_day) return false;
    if (form.recipient_mode === "new_email") {
      const v = (form.recipient_email || "").trim();
      if (!v) return false;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return false;
    }
    return true;
  }, [form]);

  const cancelEdit = () => {
    setEditingId(null);
    setForm(_EMPTY_FORM);
  };

  const startEdit = (s) => {
    setEditingId(s.schedule_id);
    setForm({
      enabled: !!s.enabled,
      frequency: s.frequency || "daily",
      weekly_day: s.weekly_day || "mon",
      recipient_mode: s.recipient_mode || "my_email",
      recipient_email: s.recipient_email || "",
      // Load persisted filters ("" means unfiltered / All).
      network_filter: s.network_filter || "",
      country_filter: s.country_filter || "",
    });
  };

  const buildPayload = (f) => ({
    enabled: !!f.enabled,
    frequency: f.frequency,
    weekly_day: f.frequency === "weekly" ? f.weekly_day : null,
    recipient_mode: f.recipient_mode,
    recipient_email: f.recipient_mode === "new_email"
      ? (f.recipient_email || "").trim() : null,
    // "" → null → backend treats as no filter (unfiltered / All).
    network_filter: f.network_filter ? f.network_filter : null,
    country_filter: f.country_filter ? f.country_filter : null,
  });

  const save = async () => {
    if (!isValid) {
      toast.error("Please review the scheduling fields — some values look invalid.");
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload(form);
      if (editingId) {
        await api.put(`/portal/report-schedules/${editingId}`, payload);
        toast.success("Schedule updated.");
      } else {
        await api.post("/portal/report-schedules", payload);
        toast.success("Schedule created.");
      }
      cancelEdit();
      await refresh();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not save the schedule.";
      toast.error(typeof msg === "string" ? msg : "Could not save the schedule.");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (s) => {
    setSaving(true);
    try {
      const payload = buildPayload({
        enabled: !s.enabled,
        frequency: s.frequency,
        weekly_day: s.weekly_day,
        recipient_mode: s.recipient_mode,
        recipient_email: s.recipient_email || "",
        network_filter: s.network_filter || "",
        country_filter: s.country_filter || "",
      });
      await api.put(`/portal/report-schedules/${s.schedule_id}`, payload);
      toast.success(`Schedule ${!s.enabled ? "enabled" : "disabled"}.`);
      await refresh();
    } catch {
      toast.error("Could not update the schedule.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    const summary = scheduleSummary(s);
    if (!window.confirm(`Delete this schedule?\n\n${summary.title}\n${summary.verbal}`)) {
      return;
    }
    setSaving(true);
    try {
      await api.delete(`/portal/report-schedules/${s.schedule_id}`);
      if (editingId === s.schedule_id) cancelEdit();
      toast.success("Schedule deleted.");
      await refresh();
    } catch {
      toast.error("Could not delete the schedule.");
    } finally {
      setSaving(false);
    }
  };

  const editingSummary = editingId ? scheduleSummary(form) : null;

  return (
    <div className="gf-card mb-4" data-testid="portal-tx-schedule-card">
      <div className="gf-card-header">
        <h3>Transaction Report Schedule</h3>
        <div className="text-xs mt-1" style={{ color: "#6B7280" }}>
          Have your Transaction Report emailed automatically — Daily,
          Weekly, or Monthly. Add as many schedules as you like; each
          one is scoped to your company only.
        </div>
      </div>
      <div className="gf-card-body space-y-4">
        {loading ? (
          <div className="text-sm" style={{ color: "#6B7280" }}>Loading…</div>
        ) : (
          <>
            {/* Section A — Saved Scheduled Reports (compact table).
                P4-CUSTOMER-TRANSACTION-REPORT-SCHEDULER — Compact UI
                Refinement (2026-09-16). Each schedule renders as ONE
                row with a Switch in STATUS + Pencil/Trash icon
                actions. Zero API/scheduler change; the Switch calls
                the same `toggle(s)` handler that already hits PUT
                `/api/portal/report-schedules/{schedule_id}` and the
                backend contract already preserves
                `last_period_end` / `last_run_id` / `last_run_at`
                (proven by `test_put_never_touches_idempotency_markers`). */}
            {schedules.length > 0 && (
              <div className="space-y-2" data-testid="portal-tx-sched-list">
                <div className="overline text-[10px]" style={{ color: "#6B7280" }}>
                  SAVED SCHEDULED REPORTS
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="portal-tx-sched-table">
                    <thead>
                      <tr style={{ color: "#6B7280", borderBottom: "1px solid #E5E7EB" }}>
                        <th className="text-left font-medium py-2 pr-3 overline whitespace-nowrap">REPORT</th>
                        <th className="text-left font-medium py-2 pr-3 overline whitespace-nowrap">SCHEDULE</th>
                        <th className="text-left font-medium py-2 pr-3 overline whitespace-nowrap">REPORT PERIOD</th>
                        <th className="text-left font-medium py-2 pr-3 overline whitespace-nowrap" data-testid="portal-tx-sched-th-network">NETWORK</th>
                        <th className="text-left font-medium py-2 pr-3 overline whitespace-nowrap" data-testid="portal-tx-sched-th-country">COUNTRY</th>
                        <th className="text-left font-medium py-2 pr-3 overline whitespace-nowrap">RECIPIENT</th>
                        <th className="text-left font-medium py-2 pr-3 overline whitespace-nowrap">STATUS</th>
                        <th className="text-right font-medium py-2 overline whitespace-nowrap">ACTIONS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {schedules.map((s) => {
                        const sum = scheduleSummary(s);
                        const recipientLabel = s.recipient_mode === "new_email"
                          ? (s.recipient_email || "—")
                          : "My Email";
                        const isEditingThis = editingId === s.schedule_id;
                        const frequencyLabel = { daily: "Daily", weekly: "Weekly", monthly: "Monthly" }[s.frequency] || s.frequency;
                        return (
                          <tr
                            key={s.schedule_id}
                            style={{ borderBottom: "1px solid #F3F4F6",
                                     background: isEditingThis ? "#F5F9FF" : "transparent" }}
                            data-testid={`portal-tx-sched-item-${s.schedule_id}`}
                          >
                            <td className="py-2 pr-3 text-sm font-medium whitespace-nowrap">{frequencyLabel}</td>
                            <td className="py-2 pr-3 whitespace-nowrap">{sum.verbal}</td>
                            <td className="py-2 pr-3 whitespace-nowrap" style={{ color: "#4B5563" }}>{sum.period}</td>
                            <td className="py-2 pr-3 whitespace-nowrap" data-testid={`portal-tx-sched-network-${s.schedule_id}`}>
                              {humanNetwork(s.network_filter)}
                            </td>
                            <td className="py-2 pr-3 whitespace-nowrap" data-testid={`portal-tx-sched-country-${s.schedule_id}`}>
                              {humanCountry(s.country_filter)}
                            </td>
                            <td className="py-2 pr-3">
                              <span data-testid={`portal-tx-sched-recipient-${s.schedule_id}`}>{recipientLabel}</span>
                            </td>
                            <td className="py-2 pr-3">
                              <div className="flex items-center gap-2">
                                <Switch
                                  checked={!!s.enabled}
                                  onCheckedChange={() => toggle(s)}
                                  disabled={saving}
                                  aria-label={s.enabled ? `Disable ${frequencyLabel} schedule` : `Enable ${frequencyLabel} schedule`}
                                  data-testid={`portal-tx-sched-toggle-${s.schedule_id}`}
                                />
                                <span
                                  className="text-[11px] whitespace-nowrap"
                                  style={{ color: s.enabled ? "#047857" : "#9CA3AF", fontWeight: 600 }}
                                  data-testid={`portal-tx-sched-status-${s.schedule_id}`}
                                >
                                  {s.enabled ? "Enabled" : "Disabled"}
                                </span>
                              </div>
                              {s.last_period_end && (
                                <div className="text-[10px] mt-1" style={{ color: "#6B7280" }}>
                                  Last sent through {s.last_period_end}
                                </div>
                              )}
                            </td>
                            <td className="py-2 text-right whitespace-nowrap">
                              <div className="inline-flex items-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => startEdit(s)}
                                  disabled={saving}
                                  title="Edit schedule"
                                  aria-label="Edit schedule"
                                  className="rounded p-1 hover:bg-zinc-100"
                                  style={{ color: "#4B5563" }}
                                  data-testid={`portal-tx-sched-edit-${s.schedule_id}`}
                                >
                                  <PencilSimple size={16}/>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => remove(s)}
                                  disabled={saving}
                                  title="Delete schedule"
                                  aria-label="Delete schedule"
                                  className="rounded p-1 hover:bg-red-50"
                                  style={{ color: "#B91C1C" }}
                                  data-testid={`portal-tx-sched-delete-${s.schedule_id}`}
                                >
                                  <Trash size={16}/>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Section B — Create / Edit form */}
            <div className="space-y-3" data-testid="portal-tx-sched-form">
              <div className="overline text-[10px]" style={{ color: "#6B7280" }}>
                {editingId ? `EDIT SCHEDULE${editingSummary ? " — " + editingSummary.title : ""}` : "ADD A NEW SCHEDULE"}
              </div>

              <div className="flex items-center gap-2">
                <input
                  id="portal-tx-sched-enabled"
                  type="checkbox"
                  checked={!!form.enabled}
                  onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                  data-testid="portal-tx-sched-enabled"
                />
                <label htmlFor="portal-tx-sched-enabled" className="text-sm">Enabled</label>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                <div>
                  <label className="overline text-[10px]">FREQUENCY</label>
                  <select
                    value={form.frequency}
                    onChange={(e) => setForm({ ...form, frequency: e.target.value })}
                    className="gf-input w-full"
                    data-testid="portal-tx-sched-frequency"
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                {form.frequency === "weekly" && (
                  <div>
                    <label className="overline text-[10px]">DELIVERY DAY</label>
                    <select
                      value={form.weekly_day}
                      onChange={(e) => setForm({ ...form, weekly_day: e.target.value })}
                      className="gf-input w-full"
                      data-testid="portal-tx-sched-weekly-day"
                    >
                      <option value="mon">Monday</option>
                      <option value="tue">Tuesday</option>
                      <option value="wed">Wednesday</option>
                      <option value="thu">Thursday</option>
                      <option value="fri">Friday</option>
                      <option value="sat">Saturday</option>
                      <option value="sun">Sunday</option>
                    </select>
                  </div>
                )}
                <div>
                  <label className="overline text-[10px]">RECIPIENT</label>
                  <div className="flex flex-col gap-1 pt-1">
                    <label className="text-sm flex items-center gap-2">
                      <input
                        type="radio"
                        name="portal-tx-sched-recipient"
                        value="my_email"
                        checked={form.recipient_mode === "my_email"}
                        onChange={() => setForm({ ...form, recipient_mode: "my_email" })}
                        data-testid="portal-tx-sched-recipient-my"
                      />
                      My Email
                    </label>
                    <label className="text-sm flex items-center gap-2">
                      <input
                        type="radio"
                        name="portal-tx-sched-recipient"
                        value="new_email"
                        checked={form.recipient_mode === "new_email"}
                        onChange={() => setForm({ ...form, recipient_mode: "new_email" })}
                        data-testid="portal-tx-sched-recipient-new"
                      />
                      New Email
                    </label>
                  </div>
                </div>
              </div>

              {form.recipient_mode === "new_email" && (
                <div>
                  <label className="overline text-[10px]">EMAIL</label>
                  <input
                    type="email"
                    value={form.recipient_email}
                    onChange={(e) => setForm({ ...form, recipient_email: e.target.value })}
                    className="gf-input w-full"
                    placeholder="ops@yourcompany.com"
                    data-testid="portal-tx-sched-email"
                  />
                </div>
              )}

              {/* P4-PORTAL-SCHEDULE-FILTER-UI R1 (Feb 2026) — additive
                  cohort filters. Same canonical backend values used
                  by the manual Portal → Transactions download. "" (All)
                  → null on the wire → unfiltered/All.

                  CUSTOMER-SCOPED R1 (Feb 2026) — the option list is
                  filtered by `scopedSchedulerFilters(customerProfile)`
                  so a customer never sees a network they aren't
                  authorized for. Reuses the SAME lane derivation the
                  Transactions & Downloads dropdown uses — there is
                  ONE customer-network authority, not one per surface. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="overline text-[10px]">NETWORK</label>
                  <select
                    value={form.network_filter}
                    onChange={(e) => {
                      const nw = e.target.value;
                      // If the currently-selected country is not
                      // available for the newly-picked network, reset
                      // to All so we never persist an impossible
                      // (network, country) pair.
                      const allowed = nw
                        ? scoped.countriesByNetwork[nw] || new Set()
                        : scoped.allCountries;
                      const nextCountry = (form.country_filter && !allowed.has(form.country_filter))
                        ? "" : form.country_filter;
                      setForm({ ...form, network_filter: nw, country_filter: nextCountry });
                    }}
                    className="gf-input w-full"
                    data-testid="portal-tx-sched-network-filter"
                  >
                    <option value="">All</option>
                    {scoped.availableNetworks.has("esso") && (
                      <option value="esso">Esso</option>
                    )}
                    {scoped.availableNetworks.has("pipeline") && (
                      <option value="pipeline">Pipeline</option>
                    )}
                    {scoped.availableNetworks.has("ta_petro") && (
                      <option value="ta_petro">TA/Petro</option>
                    )}
                    {scoped.availableNetworks.has("petro_pass") && (
                      <option value="petro_pass">Petro-Pass</option>
                    )}
                  </select>
                </div>
                <div>
                  <label className="overline text-[10px]">COUNTRY</label>
                  <select
                    value={form.country_filter}
                    onChange={(e) => setForm({ ...form, country_filter: e.target.value })}
                    className="gf-input w-full"
                    data-testid="portal-tx-sched-country-filter"
                  >
                    <option value="">All</option>
                    {(() => {
                      const allowed = form.network_filter
                        ? scoped.countriesByNetwork[form.network_filter] || new Set()
                        : scoped.allCountries;
                      return (
                        <>
                          {allowed.has("CA") && <option value="CA">Canada</option>}
                          {allowed.has("US") && <option value="US">USA</option>}
                        </>
                      );
                    })()}
                  </select>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 pt-1">
                <button
                  type="button"
                  onClick={save}
                  disabled={saving || !isValid}
                  className="gf-btn gf-btn-primary text-xs"
                  data-testid="portal-tx-sched-save"
                >
                  {editingId ? "Update Schedule" : "Save Schedule"}
                </button>
                {editingId && (
                  <button
                    type="button"
                    onClick={cancelEdit}
                    disabled={saving}
                    className="gf-btn gf-btn-outline text-xs"
                    data-testid="portal-tx-sched-cancel-edit"
                  >
                    Cancel Edit
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Customer portal only — customer-scoped searchable dropdown was
// used by legacy Card/Driver/Unit quick filters. Removed in R1A when
// the shared FROM/TO/NETWORK/UNIT tile replaced the lower filter card.

export default function CustomerTransactions() {
  const { user } = useAuth();
  const customerId = user?.customer_id || null;
  const [rows, setRows] = useState([]);

  // R1B-2 — Fetch the authenticated customer's profile so the Network
  // dropdown can hide lanes this customer isn't authorized/saved for.
  // The customer-scoped lane derivation lives in ONE helper
  // (`deriveAuthorizedLanes`) shared with the Schedule form so we
  // never end up with two independent scoping algorithms. Backend
  // stays authoritative — an unauthorized URL still 403s / returns
  // empty via customer scoping.
  const [customerProfile, setCustomerProfile] = useState(null);
  useEffect(() => {
    if (!customerId) return;
    api.get(`/customers/${customerId}`)
      .then((res) => setCustomerProfile((res && res.data) || {}))
      .catch(() => setCustomerProfile({}));
  }, [customerId]);

  const authorizedNetworks = useMemo(
    () => deriveAuthorizedLanes(customerProfile),
    [customerProfile],
  );

  // R1A — one shared filter state drives BOTH the visible transaction
  // table AND the CSV/PDF/ZIP downloads. Legacy `filters = {start,end,
  // card,driver,unit}` split has been retired; the download panel and
  // the table now consume the same object.
  //
  // CUSTOMER-PORTAL-TRANSACTION-HISTORY-CUTOFF-R1 (2026-02-09) —
  // customer transaction access is hard-floored at 2026-06-01. The FROM
  // input's `min` attribute enforces the same floor client-side; the
  // helper below normalises the default 30-day window if that window
  // crosses the cutoff so the initial UI state never shows an invalid
  // date. Server-side enforcement (customer_history_cutoff middleware +
  // portal_charts / portal_units / invoices inline clamps) is authoritative
  // — this is UX polish, not a security boundary.
  const CUSTOMER_HISTORY_MIN = "2026-06-01";
  const _clampFrom = (iso) => (iso && iso < CUSTOMER_HISTORY_MIN ? CUSTOMER_HISTORY_MIN : iso);
  const _thirtyDaysAgo = () => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return _clampFrom(d.toISOString().slice(0, 10));
  };
  const _today = () => new Date().toISOString().slice(0, 10);
  const [filters, setFilters] = useState({
    from: _thirtyDaysAgo(),
    to: _today(),
    network: "",   // "" | "esso_ca" | "pipeline" | "petro_pass_ca" | "petro_pass_us"
    country: "",   // "" = All | "CA" | "US"
    unit: "",      // "" = all units / cards
  });
  // R1B-2 — if the customer's authorized set changes and the current
  // selection is no longer valid (e.g. saved as "pipeline" but the
  // profile just resolved without pipeline), reset to Both/All. Never
  // silently keep a hidden selection driving requests.
  useEffect(() => {
    if (
      customerProfile
      && filters.network
      && !authorizedNetworks.includes(filters.network)
    ) {
      setFilters((f) => ({ ...f, network: "" }));
    }
  }, [customerProfile, authorizedNetworks, filters.network]);
  const [unitOptions, setUnitOptions] = useState([]);
  const prevUnitOptions = useRef([]);
  useEffect(() => {
    const params = filters.country ? { country: filters.country } : {};
    api.get("/portal/units", { params })
      .then((res) => {
        const units = Array.isArray(res?.data?.units) ? res.data.units : [];
        // Clear the Unit/Card only if it was a valid choice that the new
        // Country no longer offers; free-typed partial text is left alone.
        const prev = prevUnitOptions.current;
        setFilters((f) => {
          const u = (f.unit || "").trim();
          return u && prev.includes(u) && !units.includes(u) ? { ...f, unit: "" } : f;
        });
        prevUnitOptions.current = units;
        setUnitOptions(units);
      })
      .catch(() => setUnitOptions([]));
  }, [filters.country]);

  // Convert the shared state into the query object the existing
  // /api/transactions endpoint understands. Same params used to build
  // the /portal/downloads/transactions/* URLs below, so table and
  // downloads see one row population by construction.
  useEffect(() => {
    const params = {};
    if (filters.from) params.start = filters.from;
    if (filters.to) params.end = filters.to;
    // R1B-1 — Canada / ESSO is an atomic (network=esso, country=CA)
    // pair, same pattern as Petro-Pass CA/US. Sending `network=esso`
    // alone tripped the legacy Esso-umbrella expansion in
    // routes/transactions.py:3996 / :2727 (which fans out to
    // {$in:[esso,ta_petro]}) and pulled TA Petro USA rows into the
    // report. Anchoring with `country=CA` narrows the scope back to
    // Esso-CA while preserving the umbrella behaviour anywhere the
    // frontend does not send a country tag (untouched).
    const eff = effectiveTxFilters(filters);
    if (eff.network) params.network = eff.network;
    if (eff.country) params.country = eff.country;
    if (filters.unit && filters.unit.trim()) params.unit = filters.unit.trim();
    api.get("/transactions", { params }).then(({ data }) => {
      // Match the CSV/PDF/ZIP contract exactly: hide `status='duplicate'`
      // rows from the visible table. Duplicates are second-import
      // echoes the customer-facing report has always excluded (see
      // `_build_transactions_report` — May 2026 JSONS ruling).
      // Filtering client-side keeps `/api/transactions` unchanged (no
      // backend protected file touched) while restoring one-population
      // parity across table + CSV + PDF + ZIP.
      const clean = Array.isArray(data) ? data.filter((t) => t?.status !== "duplicate") : [];
      setRows(clean);
    });
  }, [filters]);

  const downloadHrefs = useMemo(() => {
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const params = new URLSearchParams({ date_from: filters.from, date_to: filters.to });
    // Petro-Pass CA / US atomic unpack — same mapping as Downloads &
    // Price Lists pages so the backend receives an identical query.
    // R1B-1 — Canada / ESSO likewise unpacks atomically (see comment
    // on the table fetch above; anchors country=CA to bypass the
    // legacy Esso-umbrella expansion for USA/TA rows).
    const eff = effectiveTxFilters(filters);
    if (eff.network) params.set("network", eff.network);
    if (eff.country) params.set("country", eff.country);
    if (filters.unit && filters.unit.trim()) params.set("unit", filters.unit.trim());
    const qs = params.toString();
    return {
      csv: `${backend}/api/portal/downloads/transactions/csv?${qs}`,
      pdf: `${backend}/api/portal/downloads/transactions/pdf?${qs}`,
      zip: `${backend}/api/portal/downloads/transactions/zip?${qs}`,
    };
  }, [filters]);
  const downloadDisabled = !filters.from || !filters.to;
  const laneFits = (lane) => !filters.country || _LANE_COUNTRY(lane) === filters.country;

  // Per-column TOTAL row (Jun 2026 — operator request, parity with the
  // CSV/PDF customer downloads). Only the dollar/volume columns are
  // summed: QTY, SUB TOTAL (customer_amount), GST/HST, PST/QST and
  // FINAL AMT. CPL (per-litre rate) is deliberately not summed because
  // a sum of per-unit prices is meaningless. Tax columns the portal
  // table doesn't surface (FET/PFT/PCT/LOCAL) aren't summed here
  // either — those still appear in the downloadable report.
  const totals = useMemo(() => {
    if (!rows.length) return null;
    const t = { qty: 0, customer_amount: 0, tax_gst_hst_fnt: 0, tax_pst_qst: 0, final_amount: 0 };
    for (const r of rows) {
      for (const k of Object.keys(t)) {
        const v = r[k];
        if (typeof v === "number" && Number.isFinite(v)) t[k] += v;
      }
    }
    return t;
  }, [rows]);

  return (
    <>
      <PageHeader title="Transactions" subtitle="PORTAL / FUEL HISTORY" testId="portal-tx-header" />

      <PortalBanner variant="transactions" />

      {/* R1A — single unified transaction filter + download tile.
          FROM/TO/NETWORK/UNIT drive BOTH the visible table below and
          the CSV/PDF/ZIP downloads (same shared `filters` state). The
          legacy lower filter card was removed; the table renders
          directly beneath this tile. Card/Driver filters intentionally
          removed per operator R1A scope. */}
      <div className="gf-card mb-4" data-testid="portal-tx-downloads-card">
        <div className="gf-card-header">
          <h3>Transactions &amp; downloads</h3>
          <div className="text-xs mt-1" style={{ color: "#6B7280" }}>
            One filter set. Filter your visible transactions and
            export the same rows as CSV / PDF, or bundle one report
            per Unit as a ZIP. Scoped to your company only.
          </div>
        </div>
        <div className="gf-card-body space-y-3">
          <div
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3"
            data-testid="portal-tx-filters-row"
          >
            <div>
              <label className="overline text-[10px]">FROM</label>
              <input
                type="date"
                value={filters.from}
                min={CUSTOMER_HISTORY_MIN}
                max={filters.to || undefined}
                onChange={(e) => setFilters({ ...filters, from: _clampFrom(e.target.value) })}
                className="gf-input w-full"
                data-testid="portal-tx-dl-from"
              />
            </div>
            <div>
              <label className="overline text-[10px]">TO</label>
              <input
                type="date"
                value={filters.to}
                min={filters.from || undefined}
                onChange={(e) => setFilters({ ...filters, to: e.target.value })}
                className="gf-input w-full"
                data-testid="portal-tx-dl-to"
              />
            </div>
            <div>
              <label className="overline text-[10px]">NETWORK</label>
              <select
                value={filters.network}
                onChange={(e) => setFilters({ ...filters, network: e.target.value })}
                className="gf-input w-full"
                data-testid="portal-tx-dl-network"
              >
                <option value="">All networks</option>
                {authorizedNetworks.includes("esso_ca") && laneFits("esso_ca") && (
                  <option value="esso_ca" data-testid="portal-tx-dl-network-opt-esso-ca">
                    Canada / ESSO
                  </option>
                )}
                {authorizedNetworks.includes("pipeline") && laneFits("pipeline") && (
                  <option value="pipeline" data-testid="portal-tx-dl-network-opt-pipeline">
                    Pipeline
                  </option>
                )}
                {authorizedNetworks.includes("petro_pass_ca") && laneFits("petro_pass_ca") && (
                  <option value="petro_pass_ca" data-testid="portal-tx-dl-network-opt-petro-pass-ca">
                    Petro-Pass Canada · CAD/L
                  </option>
                )}
                {authorizedNetworks.includes("petro_pass_us") && laneFits("petro_pass_us") && (
                  <option value="petro_pass_us" data-testid="portal-tx-dl-network-opt-petro-pass-us">
                    Petro-Pass USA · USD/GAL
                  </option>
                )}
              </select>
            </div>
            <div>
              <label className="overline text-[10px]">COUNTRY</label>
              <select
                value={filters.country}
                onChange={(e) => {
                  const country = e.target.value;
                  setFilters((f) => ({
                    ...f, country,
                    network: f.network && country && _LANE_COUNTRY(f.network) !== country ? "" : f.network,
                  }));
                }}
                className="gf-input w-full"
                data-testid="portal-tx-dl-country"
              >
                <option value="" data-testid="portal-tx-dl-country-opt-all">All</option>
                <option value="CA" data-testid="portal-tx-dl-country-opt-ca">Canada</option>
                <option value="US" data-testid="portal-tx-dl-country-opt-us">USA</option>
              </select>
            </div>
            <div>
              <label className="overline text-[10px]">UNIT / CARD</label>
              {/* R1B-3 — one-click clear. Rendered only when a Unit
                  value is present; single click empties `filters.unit`
                  which the shared state pipeline propagates to table
                  + CSV + PDF + ZIP hrefs in the same render tick. */}
              <div className="relative">
                <input
                  list="portal-tx-dl-unit-opts"
                  value={filters.unit}
                  onChange={(e) => setFilters({ ...filters, unit: e.target.value })}
                  placeholder="All units"
                  className="gf-input w-full"
                  style={filters.unit ? { paddingRight: 28 } : undefined}
                  data-testid="portal-tx-dl-unit"
                />
                {filters.unit && (
                  <button
                    type="button"
                    aria-label="Clear unit filter"
                    onClick={() => setFilters({ ...filters, unit: "" })}
                    className="absolute inset-y-0 right-1 my-auto inline-flex items-center justify-center rounded-md p-1 hover:bg-zinc-100 focus:outline-none focus:ring-1 focus:ring-zinc-300"
                    style={{ height: 22, width: 22 }}
                    data-testid="portal-tx-dl-unit-clear"
                  >
                    <X size={12} weight="bold" style={{ color: "#6B7280" }} />
                  </button>
                )}
              </div>
              <datalist id="portal-tx-dl-unit-opts">
                {unitOptions.map((u) => (<option key={u} value={u} />))}
              </datalist>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            <div className="overline text-[10px]" style={{ color: "#6B7280" }}>
              <span data-testid="portal-tx-record-count">{rows.length}</span>{" "}RECORDS SHOWN
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="overline text-[10px] mr-1" style={{ color: "#6B7280" }}>DOWNLOAD</span>
              <a
                href={downloadDisabled ? undefined : downloadHrefs.csv}
                target="_blank"
                rel="noopener noreferrer"
                aria-disabled={downloadDisabled}
                className="gf-btn gf-btn-outline inline-flex items-center gap-2 text-xs no-underline"
                style={downloadDisabled ? { opacity: 0.5, pointerEvents: "none" } : undefined}
                data-testid="portal-tx-dl-csv"
              >
                <FileCsv size={16} weight="duotone" style={{ color: "#1A3FBA" }} />
                CSV
                <Download size={12} style={{ color: "#9CA3AF" }} />
              </a>
              <a
                href={downloadDisabled ? undefined : downloadHrefs.pdf}
                target="_blank"
                rel="noopener noreferrer"
                aria-disabled={downloadDisabled}
                className="gf-btn gf-btn-outline inline-flex items-center gap-2 text-xs no-underline"
                style={downloadDisabled ? { opacity: 0.5, pointerEvents: "none" } : undefined}
                data-testid="portal-tx-dl-pdf"
              >
                <FilePdf size={16} weight="duotone" style={{ color: "#1A3FBA" }} />
                PDF
                <Download size={12} style={{ color: "#9CA3AF" }} />
              </a>
              <a
                href={downloadDisabled ? undefined : downloadHrefs.zip}
                target="_blank"
                rel="noopener noreferrer"
                aria-disabled={downloadDisabled}
                className="gf-btn gf-btn-outline inline-flex items-center gap-2 text-xs no-underline"
                style={downloadDisabled ? { opacity: 0.5, pointerEvents: "none" } : undefined}
                data-testid="portal-tx-dl-zip"
              >
                <FileZip size={16} weight="duotone" style={{ color: "#1A3FBA" }} />
                Per-Unit ZIP
                <Download size={12} style={{ color: "#9CA3AF" }} />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* P4-CUSTOMER-TRANSACTION-REPORT-SCHEDULER R1 (2026-09-15) —
          compact scheduling card. Preserves the visual language of the
          existing filter/downloads card above. Zero change to the
          existing report table, filters, downloads, palette, or fonts. */}
      <TransactionReportScheduler customerProfile={customerProfile} />

      <div className="gf-card" data-testid="portal-tx-table-card">
        <table className="gf-table" data-testid="portal-tx-table">
          <thead><tr><th>DATE</th><th>CARD</th><th>UNIT</th><th>STATION</th><th>PRODUCT</th><th className="text-right">QTY</th><th className="text-right">CPL</th><th className="text-right">SUB TOTAL</th><th className="text-right">GST/HST</th><th className="text-right">PST/QST</th><th className="text-right">FINAL AMT</th><th>STATUS</th></tr></thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.transaction_id}>
                <td className="font-mono text-xs">{t.tran_date}</td>
                <td className="font-mono">{formatCard(t.card_number, t.network, t.card_last4)}</td>
                <td className="font-mono text-xs">{t.unit || "—"}</td>
                <td className="text-xs">{t.station_name}</td>
                <td className="font-mono text-xs">{displayProduct(t.product, t.network)}</td>
                <td className="gf-num">{t.qty?.toFixed(2)}</td>
                <td className="gf-num">{t.customer_cpl?.toFixed(4)}</td>
                <td className="gf-num">${t.customer_amount?.toFixed(2)}</td>
                <td className="gf-num">{t.tax_gst_hst_fnt != null ? `$${t.tax_gst_hst_fnt.toFixed(2)}` : "—"}</td>
                <td className="gf-num">{t.tax_pst_qst != null ? `$${t.tax_pst_qst.toFixed(2)}` : "—"}</td>
                <td className="gf-num font-semibold" data-testid={`portal-tx-final-${t.transaction_id}`}>
                  {t.final_amount != null ? `$${t.final_amount.toFixed(2)}` : "—"}
                </td>
                <td><Badge variant={t.status === "invoiced" ? "success" : "info"}>{t.status}</Badge></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={12} className="text-center text-zinc-400 py-8">No transactions yet.</td></tr>}
          </tbody>
          {totals && (
            <tfoot>
              <tr
                className="bg-zinc-100 font-semibold border-t-2 border-zinc-900"
                data-testid="portal-tx-totals-row"
              >
                <td colSpan={5} className="text-right pr-3 text-xs uppercase tracking-wide">
                  TOTAL
                </td>
                <td className="gf-num" data-testid="portal-tx-total-qty">
                  {totals.qty.toFixed(2)}
                </td>
                <td className="gf-num text-zinc-400">—</td>
                <td className="gf-num" data-testid="portal-tx-total-subtotal">
                  ${totals.customer_amount.toFixed(2)}
                </td>
                <td className="gf-num" data-testid="portal-tx-total-gst">
                  ${totals.tax_gst_hst_fnt.toFixed(2)}
                </td>
                <td className="gf-num" data-testid="portal-tx-total-pst">
                  ${totals.tax_pst_qst.toFixed(2)}
                </td>
                <td className="gf-num" data-testid="portal-tx-total-final">
                  ${totals.final_amount.toFixed(2)}
                </td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </>
  );
}
